/**
 * Marketing rules: who is in an audience, who is reachable, what a campaign
 * achieved, and how a journey advances.
 *
 * Audiences are *live segments*, not stored lists — `audienceOf` is evaluated
 * against the current book every time, so a campaign always sends to whoever
 * qualifies today rather than whoever qualified when it was written.
 *
 * As with the rest of `domain/`, nothing here imports React.
 */
import { parseDate, daysAgo, daysTo, formatDate, startOfDay } from '../lib/dates.js';
import { money, digitsOf } from '../lib/format.js';
import { digitsOnly } from '../lib/phone.js';
import { linkKey } from '../lib/clickmap.js';
import { deriveClient } from './derive.js';
import { amountOf, mrrOf } from './pricing.js';
import { isOpen, companyName } from './crm.js';
import { CLIENT_ONLY_FLAGS, CONTACT_ONLY_FLAGS, EXITS, channelLabel } from '../constants/marketing.js';

const firstName = n => String(n || '').trim().split(/\s+/)[0] || 'there';

/**
 * The body as it should be stored, with every inline image back to its `cid:`.
 *
 * The editor shows a picture as a data: URL or an object URL so the author can
 * see it. Neither belongs in a saved campaign: an object URL dies with the
 * tab, and a data: URL is a third of a megabyte of base64 that bloats the
 * marketing document and is stripped by Gmail and Outlook anyway. `data-cid`
 * survives every trip through contenteditable, so it is what the src is put
 * back from.
 *
 * This lives here, not in the editor, because the editor is not the only way a
 * body reaches storage — a duplicated campaign and a template both carry one
 * too, and all of them must be normalised the same way.
 */
export const storableBody = html => String(html || '').replace(/<img[^>]*>/gi, tag => {
  const cid = (tag.match(/data-cid="(x[0-9a-f]{32})"/i) || [])[1];
  if (!cid) return tag;

  /*
   * Every src is taken out and exactly one put back, rather than the first one
   * being rewritten in place. Replacing only the first leaves any others
   * behind — and a tag that already carried a `cid:` then grew a second and a
   * third src on each pass, with the data: URL still sitting at the end where
   * the mail client found it. Rebuilding the attribute is idempotent: running
   * this twice cannot differ from running it once.
   */
  const stripped = tag.replace(/\s+src="[^"]*"/gi, '');
  return stripped.replace(/^<img/i, `<img src="cid:${cid}"`);
});

/**
 * Everyone who qualifies for an audience right now, each with the merge
 * variables a message can use.
 */
export function audienceOf(audience, { S, rows, crm, mkt }) {
  const a = audience || {};
  const out = [];
  const currency = S.currency || 'AED';

  if ((a.src || 'clients') === 'clients') {
    rows.forEach(r => {
      const d = deriveClient(S, r);
      if (d.excluded) return;
      if (a.status && r.status !== a.status) return;
      if (a.plan && String(r.plan || '') !== a.plan) return;
      if (a.type && d.type !== a.type) return;
      if (a.owner && r.handler !== a.owner) return;

      const f = a.flag;
      if (f === 'churned') {
        const churned = ['Churned', 'Expired'].includes(r.status) || d.flags.some(x => x.tag === 'expired');
        if (!churned) return;
      } else if (f === 'free') {
        if (String(r.plan || '').toUpperCase() !== 'FREE') return;
      } else if (CONTACT_ONLY_FLAGS.includes(f)) {
        return;                                    // contact-only filter on a client source
      } else if (f && !d.flags.some(x => x.tag === f)) return;

      if (a.inactiveDays) {
        const last = parseDate(r.lastActivity);
        if (!last || daysAgo(last) < +a.inactiveDays) return;
      }

      out.push({
        kind: 'client', id: r.id, name: r.name, email: r.email, phone: r.phone,
        vars: {
          name: r.name,
          first: firstName(r.contact || r.name),
          company: r.name,
          plan: r.plan || '',
          amount: money(d.amt || 0, currency),
          days: String(d.dT !== null ? Math.abs(d.dT) : (d.dueIn !== null ? Math.abs(d.dueIn) : 0)),
          owner: r.handler || '',
          promo: r.promo || '',
          contact: r.contact || ''
        }
      });
    });
    return out;
  }

  /*
   * Marketing's own address book. There is no pipeline and no ledger behind
   * these, so the deal-shaped flags do not apply — a name and an address is
   * all there is, and the subscription status is the only thing to filter on.
   */
  if (a.src === 'marketing') {
    (mkt.contacts || []).forEach(c => {
      if (a.status && c.status !== a.status) return;
      if (a.flag) return;                          // no ledger or deal history to test

      out.push({
        kind: 'mktContact', id: c.id, name: c.name, email: c.email, phone: c.phone,
        vars: {
          name: c.name,
          first: firstName(c.name),
          company: c.company || '',
          plan: '',
          amount: money(0, currency),
          days: '0',
          owner: S.senderName || '',
          promo: '',
          contact: c.name
        }
      });
    });
    return out;
  }

  crm.contacts.forEach(c => {
    const deals = crm.deals.filter(d => d.contact === c.id);
    if (a.status && c.status !== a.status) return;
    if (a.owner && c.owner !== a.owner) return;

    const f = a.flag;
    if (f === 'open-deal' && !deals.some(isOpen)) return;
    if (f === 'won' && !deals.some(d => d.stage === 'Won')) return;
    if (f === 'new-lead' && (c.status !== 'New' || deals.length)) return;
    if (CLIENT_ONLY_FLAGS.includes(f)) return;     // client-only filter on a contact source

    const top = [...deals].sort((x, y) => (+y.value || 0) - (+x.value || 0))[0];
    out.push({
      kind: 'contact', id: c.id, name: c.name, email: c.email, phone: c.phone,
      vars: {
        name: c.name,
        first: firstName(c.name),
        company: companyName(crm, c.cid),
        plan: (top && top.plan) || '',
        amount: money((top && top.value) || 0, currency),
        days: '0',
        owner: c.owner || '',
        promo: '',
        contact: c.name
      }
    });
  });

  return out;
}

export const isOptedOut = (mkt, r) =>
  mkt.optouts.includes(String(r.email || '').toLowerCase()) || mkt.optouts.includes(digitsOf(r.phone));

/**
 * Why one recipient cannot be reached on a channel, or null if they can.
 *
 * The reasons are separated rather than collapsed into a yes/no because a
 * campaign screen that says "1 skipped" and leaves the person guessing between
 * a missing address, an opt-out and an unsubscribe is no help at all: each one
 * is fixed somewhere different.
 */
export function unreachableBecause(r, channel, ctx) {
  const { S, mkt, rows, crm } = ctx;
  if (isOptedOut(mkt, r)) return 'optout';

  /*
   * A marketing contact carries its own subscription state, which the opt-out
   * list does not know about: an unsubscribe and a bounced address are both
   * refusals, and neither should be sent to.
   */
  if (r.kind === 'mktContact') {
    const record = (mkt.contacts || []).find(x => x.id === r.id);
    if (record && record.status === 'Unsubscribed') return 'unsubscribed';
    if (record && record.status === 'Bounced') return 'bounced';
  }

  if ((S.mkt || {}).consentOnly) {
    const record = rows.find(x => x.id === r.id)
      || crm.contacts.find(x => x.id === r.id)
      || (mkt.contacts || []).find(x => x.id === r.id)
      || {};
    const given = String(record.consent || '').split(';').map(x => x.trim());
    if (!given.includes(channel)) return 'consent';
  }

  if (channel === 'email' && !r.email) return 'noEmail';

  if (channel === 'whatsapp' || channel === 'sms') {
    const digits = digitsOf(r.phone);
    if (!digits) return 'noPhone';
    /* Fewer than eleven digits is a number with its country code left off.
       Chatlio takes it, Meta drops it without a word - so it is not reachable,
       and the row must say so before anybody pays for the attempt. */
    if (digits.length < 11) return 'noCountry';
    /* WhatsApp has already refused this number, and said so on the webhook.
       Sending again costs the same and lands the same. */
    if (channel === 'whatsapp' && waRefused(ctx, digits)) return 'waFailed';
  }
  return null;
}

/**
 * Has WhatsApp refused this number?
 *
 * Read from the server-owned message log, where the webhook writes every
 * refusal. A later success to the same number lifts it: a number that failed
 * in June and was delivered to in August is not dead.
 */
function waRefused(ctx, digits) {
  const log = ctx.waMessages || [];
  for (const m of log) {
    if (m.direction !== 'out' || m.phone !== digits) continue;
    // The log arrives newest first, so the first status found is the latest.
    if (m.status === 'delivered' || m.status === 'read' || m.status === 'sent') return false;
    if (m.status === 'failed') return waPermanent(m.error);
  }
  return false;
}

/*
 * A failure that is the number's fault, not the moment's.
 *
 * Meta refuses a message for two very different reasons. One is permanent -
 * the number is not on WhatsApp, or is invalid - and sending again wastes a
 * conversation to the same certain failure. The other is temporary: it is
 * pacing marketing templates to somebody who has not engaged ("healthy
 * ecosystem engagement"), or rate-limiting the sender for a moment. Those
 * clear on their own, and a number thrown out for one of them would be lost
 * for good over a passing throttle. So only the permanent kind blocks.
 */
const WA_TEMPORARY = /healthy ecosystem|re-?engag|rate.?limit|throttl|too many|try again|temporar|spam rate|pacing/i;

function waPermanent(error) {
  const text = String(error || '');
  if (!text) return false;            // no reason given: do not assume the worst
  return !WA_TEMPORARY.test(text);
}

/** How each reason reads on screen, singular and plural. */
const REASON_TEXT = {
  optout: ['on the opt-out list', 'on the opt-out list'],
  unsubscribed: ['unsubscribed', 'unsubscribed'],
  bounced: ['a bounced address', 'bounced addresses'],
  consent: ['without recorded consent', 'without recorded consent'],
  noEmail: ['with no email address', 'with no email address'],
  noPhone: ['with no phone number', 'with no phone number'],
  noCountry: ['with no country code on the number', 'with no country code on the number'],
  waFailed: ['refused by WhatsApp before', 'refused by WhatsApp before']
};

/** The reason, as a short label for one row. */
export const reasonLabel = why => ({
  optout: 'opted out',
  unsubscribed: 'unsubscribed',
  bounced: 'bounced',
  consent: 'no consent',
  noEmail: 'no email',
  noPhone: 'no phone',
  noCountry: 'no country code',
  waFailed: 'WhatsApp refused it'
})[why] || 'cannot be reached';

/**
 * Narrow an audience to those actually contactable on a channel — an email
 * campaign cannot reach someone with no email address, and nobody who has
 * opted out is contacted on any channel.
 */
export function reachable(list, channel, ctx) {
  return list.filter(r => !unreachableBecause(r, channel, ctx));
}

/**
 * The same split, with the reasons counted — what a screen needs to explain
 * itself. `notes` is ready to print: "1 unsubscribed", "2 with no email
 * address", worst first.
 */
export function reachSummary(list, channel, ctx) {
  const ok = [];
  const counts = {};

  list.forEach(r => {
    const why = unreachableBecause(r, channel, ctx);
    if (!why) { ok.push(r); return; }
    counts[why] = (counts[why] || 0) + 1;
  });

  const notes = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([why, n]) => `${n} ${(REASON_TEXT[why] || [why, why])[n > 1 ? 1 : 0]}`);

  return { ok, counts, skipped: list.length - ok.length, notes };
}

/** Substitute {{merge}} fields for one recipient. Unknown fields are left alone. */
export const applyMerge = (text, record) =>
  String(text || '').replace(/\{\{(\w+)\}\}/g, (m, k) =>
    (record.vars && record.vars[k] !== undefined ? record.vars[k] : m));

/**
 * Everyone a campaign targets: its segment, plus anyone named individually.
 *
 * A segment is the normal answer and stays right as the book moves, but a
 * one-off sometimes needs a handful of named people that no rule describes.
 * The two add together rather than replace each other, and every screen that
 * has to agree on who gets a message comes through here — the wizard's reach
 * figure, the send console's list and the analytics all read the same set.
 */
export function campaignRecipients(campaign, ctx) {
  const c = campaign || {};
  const audience = ctx.mkt.audiences.find(a => a.id === c.audience);
  const fromSegment = audience ? audienceOf(audience, ctx) : [];

  const named = c.contacts || [];
  if (!named.length) return fromSegment;

  // Look the named ones up in the same shape a segment produces, so merge
  // fields resolve for them exactly as they do for everybody else.
  const already = new Set(fromSegment.map(r => r.id));
  /*
   * The wizard names people from marketing's own list, but campaigns written
   * before it existed named sales contacts and clients, so all three sources
   * are looked up — an old campaign keeps reaching exactly who it always did.
   */
  const pool = audienceOf({ src: 'marketing' }, ctx)
    .concat(audienceOf({ src: 'clients' }, ctx))
    .concat(audienceOf({ src: 'contacts' }, ctx));
  const byId = new Map(pool.map(r => [r.id, r]));

  return fromSegment.concat(
    named.filter(id => !already.has(id)).map(id => byId.get(id)).filter(Boolean)
  );
}

/**
 * Which campaign each reply is answering.
 *
 * Mail leaves exactly one exact thread behind: the Message-ID a campaign went
 * out with, quoted back by the answer in In-Reply-To. Where the answer carries
 * it there is nothing to work out — and a second campaign to somebody who
 * replied to the first can no longer inherit that reply, which is what the
 * address on its own kept doing.
 *
 * Not every client sends one, and no send made before they were recorded has
 * one to be named by. So two weaker tests stand behind it. The subject comes
 * first: every client quotes the original back under a "Re:", which is far
 * narrower than knowing only that this person has written to us before. The
 * address is last, and files the reply under the most recent campaign that
 * reached it before the answer arrived. That one is a guess, and the screen
 * says so.
 *
 * One rule keeps the fallback from undoing the fix. An answer naming a message
 * we did not send cannot be answering a send we *could* have matched exactly,
 * so those sends are out of its running and only the ones from before message
 * ids were recorded remain. Without it, every campaign after this change
 * inherits the whole history of the address all over again.
 *
 * A reply matching nothing is in the map nowhere: somebody writing in of their
 * own accord is not a campaign's doing, and no campaign should claim them.
 */
const REPLY_PREFIX = /^(re|fw|fwd|aw|sv|vs|tr|antwort)\s*(\[\d+\])?\s*:\s*/i;

/**
 * A subject with the reply markers taken off, for comparing.
 *
 * "Re: Re: Fwd: Your first import" and "Your first import" are the same
 * conversation, and every client stacks its own prefix in its own language.
 */
export function bareSubject(value) {
  let out = String(value || '').trim();
  let before;

  do {
    before = out;
    out = out.replace(REPLY_PREFIX, '').trim();
  } while (out !== before);

  return out.replace(/\s+/g, ' ').toLowerCase();
}

export function replyOwners(ctx) {
  const out = new Map();
  const inbox = ctx.replies || [];
  if (!inbox.length) return out;

  /** Message-ID we sent -> the campaign that sent it. */
  const byMid = new Map();
  /** Address we wrote to -> every send that reached it. */
  const byAddress = new Map();

  ((ctx.mkt && ctx.mkt.campaigns) || []).forEach(c => {
    const people = new Map(campaignRecipients(c, ctx).map(r => [r.id, r]));

    (c.sends || []).forEach(send => {
      if (send.status !== 'sent') return;
      if (send.mid) byMid.set(send.mid, c.id);

      const who = people.get(send.rid);
      const address = String((who && who.email) || '').trim().toLowerCase();
      if (!address) return;

      if (!byAddress.has(address)) byAddress.set(address, []);
      byAddress.get(address).push({
        campaignId: c.id,
        mid: send.mid || '',
        // The subject as this person received it, merge fields and all, so a
        // reply quoting it back can be recognised.
        subject: bareSubject(applyMerge(c.subject || '', who || {})),
        at: parseDate(send.sentAt || send.at)
      });
    });
  });

  inbox.forEach(r => {
    const thread = String(r.in_reply_to || '').trim();

    if (thread && byMid.has(thread)) {
      out.set(r.id, { campaignId: byMid.get(thread), how: 'thread' });
      return;
    }

    const address = String(r.from_email || '').trim().toLowerCase();
    const came = new Date(r.received_at);

    const plausible = (byAddress.get(address) || [])
      .filter(send => {
        /*
         * An answer naming a message we did not send cannot be answering a
         * send we could have matched exactly, so those are out of its
         * running — only the sends from before message ids were recorded
         * remain. Without this every campaign from here on inherits the whole
         * history of the address all over again.
         */
        if (thread && send.mid) return false;
        // A send stamped with a date and no time would otherwise refuse every
        // reply that arrived the same day, which is most of them.
        return !send.at || Number.isNaN(+came) || came >= startOfDay(send.at);
      })
      .sort((a, b) => (b.at || 0) - (a.at || 0));

    /*
     * The subject, before the address. Every client quotes the original
     * subject back under a "Re:", which is far narrower evidence than "this
     * person has written to us before" — and it is all there is for sends
     * made before message ids were recorded.
     */
    const answered = bareSubject(r.subject);
    const bySubject = answered
      ? plausible.find(send => send.subject && send.subject === answered)
      : null;

    if (bySubject) {
      out.set(r.id, { campaignId: bySubject.campaignId, how: 'subject' });
      return;
    }

    if (plausible[0]) out.set(r.id, { campaignId: plausible[0].campaignId, how: 'address' });
  });

  return out;
}

/** Everything that came back to one campaign: replies, bounces and all. */
export function campaignReplies(campaign, ctx, owners) {
  const map = owners || replyOwners(ctx);
  return (ctx.replies || []).filter(r => {
    const owner = map.get(r.id);
    return !!owner && owner.campaignId === campaign.id;
  });
}

/**
 * Which of a campaign's recipients actually wrote back.
 *
 * One campaign's share of the mailbox, by address, so the count and the list
 * on screen can never disagree about who answered.
 */
export function repliedAddresses(campaign, ctx) {
  const out = new Set();

  campaignReplies(campaign, ctx).forEach(r => {
    if (r.kind !== 'reply') return;
    const address = String(r.from_email || '').trim().toLowerCase();
    if (address) out.add(address);
  });

  return out;
}

/**
 * Marketing contacts who have since become clients.
 *
 * A campaign to the marketing list could never show a conversion: the counting
 * looked each recipient up in the client ledger by id, and a marketing contact
 * has an id of its own that no client shares. So it always found nothing.
 *
 * The address is the join instead. Somebody emailed on the 3rd who appears on
 * the ledger on the 5th signed up after being written to — which is exactly
 * what a conversion is. Registering *before* the send is not: they were
 * already a client and the campaign cannot claim them.
 */
export function convertedAddresses(campaign, ctx) {
  const sends = (campaign.sends || []).filter(s => s.status === 'sent');
  if (!sends.length) return new Map();

  const when = new Map();
  campaignRecipients(campaign, ctx).forEach(r => {
    const s = sends.find(x => x.rid === r.id);
    const address = String(r.email || '').trim().toLowerCase();
    if (s && address) when.set(address, parseDate(s.at));
  });

  const out = new Map();

  (ctx.rows || []).forEach(client => {
    const address = String(client.email || '').trim().toLowerCase();
    if (!address || !when.has(address)) return;

    // `reg_on` is when the clients app first saw them; a payment counts too,
    // for an account that existed but only started paying after the send.
    const joined = parseDate(client.reg_on || client.regOn);
    const paid = parseDate(client.lastPaid);
    const sentOn = when.get(address);
    if (!sentOn) return;

    const after = (joined && joined >= startOfDay(sentOn))
      || (paid && paid >= startOfDay(sentOn));

    if (after) out.set(address, client);
  });

  return out;
}

/**
 * What the recipients' own mail clients reported: opens and clicks.
 *
 * Counted per person, not per event. Somebody who opens a message four times
 * has opened it once for the purpose of an open rate — the repeat says they
 * came back, which is a different and rarer question, so both are returned.
 *
 * An open is a floor and never a count. Gmail and Outlook block images by
 * default, so a message read with images off is a message read that this
 * cannot see. A click is the firmer signal: somebody pressed something.
 */
/*
 * WhatsApp's own ladder, in the order a message climbs it. A row's rank is
 * where the highest status it has reached sits, so a stale "sent" webhook
 * arriving after "read" cannot pull it back down.
 */
const WA_RANK = { accepted: 0, sent: 1, delivered: 2, read: 3, failed: 4 };

/**
 * What became of a WhatsApp campaign, read from the message log the webhook
 * writes.
 *
 * WhatsApp does not have opens or clicks; it has delivery, a genuine read
 * receipt from the recipient's phone, and a specific reason when Meta refuses.
 * So the numbers here are different from email's, and truer: a read is a read,
 * not a pixel that may or may not have loaded.
 *
 * Each recipient's latest status wins, because a campaign can be sent again
 * and a number can climb the ladder over several webhooks.
 */
export function waStatus(campaign, ctx) {
  const log = (ctx.waMessages || []).filter(m => m.direction === 'out' && m.campaign_id === campaign.id);

  const byRid = new Map();
  log.forEach(m => {
    const rank = WA_RANK[m.status] == null ? 0 : WA_RANK[m.status];
    const held = byRid.get(m.rid);
    if (!held || rank >= (WA_RANK[held.status] == null ? 0 : WA_RANK[held.status])) {
      byRid.set(m.rid, m);
    }
  });

  const rows = [...byRid.values()];
  const count = st => rows.filter(m => m.status === st).length;

  const sent = rows.length;
  const delivered = rows.filter(m => WA_RANK[m.status] >= WA_RANK.delivered && m.status !== 'failed').length;
  const read = count('read');
  const failed = count('failed');

  return {
    byRid,
    sent,
    delivered,
    read,
    failed,
    accepted: count('accepted') + count('sent'),
    deliveryRate: sent ? Math.round((delivered / sent) * 100) : null,
    readRate: sent ? Math.round((read / sent) * 100) : null
  };
}

/**
 * WhatsApp replies to one campaign.
 *
 * A number can be reached by several campaigns, and a reply does not say
 * which one it answers. It is taken to answer the last thing we sent that
 * number before it arrived: a reply from Saturday cannot belong to a campaign
 * created on Monday, and once a newer campaign has reached the same number,
 * anything after that is the newer campaign's. Matched by number, so "+91 …"
 * and "91…" are the same person.
 *
 * Each reply carries the name the campaign's audience had for that number,
 * where it had one.
 */
export function waReplies(campaign, ctx) {
  const msgs = ctx.waMessages || [];
  /* The log's stamps are full ISO strings; parseDate would round them to the
     minute and read them as local time, which is enough to reorder a reply
     and the send it followed. */
  const when = v => { const d = new Date(v); return isNaN(d) ? 0 : d.getTime(); };
  const out = msgs.filter(m => m.direction === 'out' && m.campaign_id && digitsOnly(m.phone));
  if (!out.some(m => m.campaign_id === campaign.id)) return [];

  /* Every outbound, newest first, per number - so the send a reply follows is
     the first one at or before it. */
  const sendsTo = new Map();
  out.forEach(m => {
    const key = digitsOnly(m.phone);
    if (!sendsTo.has(key)) sendsTo.set(key, []);
    sendsTo.get(key).push({ at: when(m.at), campaignId: m.campaign_id });
  });
  sendsTo.forEach(list => list.sort((a, b) => b.at - a.at));

  const names = new Map();
  campaignRecipients(campaign, ctx).forEach(r => {
    const key = digitsOnly(r.phone);
    if (key && r.name && !names.has(key)) names.set(key, r.name);
  });

  return msgs
    .filter(m => m.direction === 'in')
    .filter(m => {
      const key = digitsOnly(m.phone);
      const at = when(m.at);
      const before = (sendsTo.get(key) || []).find(x => x.at <= at);
      return !!before && before.campaignId === campaign.id;
    })
    .map(m => ({ ...m, name: names.get(digitsOnly(m.phone)) || '' }));
}

export function campaignEngagement(campaign, ctx) {
  /*
   * Only people we have a record of writing to.
   *
   * An open or a click from somebody with no send behind it cannot be counted
   * against "sent" - and it happens: a recipient is taken off the list, a
   * campaign is edited and re-sent, a test goes out to an address that never
   * joins the audience. Counting them anyway produced a read rate of 125%,
   * which is not a measurement of anything.
   */
  const sentTo = new Set((campaign.sends || [])
    .filter(x => x.status === 'sent')
    .map(x => x.rid));

  const mine = (ctx.events || [])
    .filter(e => e.campaign_id === campaign.id && sentTo.has(e.rid));

  const loadedImages = new Set();
  const clickedBy = new Set();
  const byLink = new Map();

  mine.forEach(e => {
    if (e.kind === 'open') { loadedImages.add(e.rid); return; }
    if (e.kind !== 'click') return;

    clickedBy.add(e.rid);
    const url = String(e.url || '');
    if (!url) return;

    /* Keyed on the form two spellings agree on, so a trailing slash or an
       escaped ampersand does not split one link into two rows that each look
       half as popular - and so the click map can find it in the copy. */
    const key = linkKey(url);
    if (!byLink.has(key)) byLink.set(key, { key, url, clicks: 0, people: new Set() });
    const link = byLink.get(key);
    link.clicks += 1;
    link.people.add(e.rid);
  });

  const sent = sentTo.size;

  /*
   * Somebody who clicked has read the message, whether or not their mail
   * client ever fetched the pixel — and Gmail and Outlook block images by
   * default, so the pixel alone reports plenty of read messages as unopened.
   * Counting a clicker as a reader corrects a known undercount; it does not
   * invent one, because pressing a link is a firmer act than loading a
   * picture.
   */
  const openedBy = new Set([...loadedImages, ...clickedBy]);

  return {
    sent,
    opens: mine.filter(e => e.kind === 'open').length,
    clicks: mine.filter(e => e.kind === 'click').length,
    opened: openedBy.size,
    // How many of those were seen only because they clicked, which is what to
    // say on screen rather than letting the two look like the same evidence.
    openedByClickOnly: [...clickedBy].filter(rid => !loadedImages.has(rid)).length,
    clicked: clickedBy.size,
    openRate: sent ? Math.round((openedBy.size / sent) * 100) : null,
    clickRate: sent ? Math.round((clickedBy.size / sent) * 100) : null,
    openedBy,
    clickedBy,
    links: [...byLink.values()]
      .map(l => ({ key: l.key, url: l.url, clicks: l.clicks, people: l.people.size }))
      .sort((a, b) => b.clicks - a.clicks)
  };
}

/**
 * What a campaign achieved.
 *
 * There is no tracking pixel here, so "replied" and "converted" are inferred
 * from the ledger: a client contacted after the send date counts as a reply, a
 * payment after it counts as a conversion. That is a real signal rather than a
 * fabricated one, and the Analytics screen says so.
 */
/**
 * What became of a push campaign, from the FCM send log.
 *
 * A topic send is a single row: it reached whoever subscribed, and Firebase
 * cannot say how many, so "sent" here means the notification was accepted for
 * the topic, not a headcount. A token campaign is one row per device, each
 * accepted or failed. There is no delivered or read - FCM does not report a
 * phone showing or a person tapping - so those are not invented.
 */
export function pushStats(campaign, ctx) {
  const log = (ctx.pushMessages || []).filter(m => m.campaign_id === campaign.id);
  const sent = log.filter(m => m.status === 'accepted').length;
  const failed = log.filter(m => m.status === 'failed').length;
  const topics = [...new Set(log.filter(m => m.target === 'topic').map(m => m.topic))];
  return { sent, failed, topics, rows: log };
}

export function campaignStats(campaign, ctx) {
  const { S, rows, crm } = ctx;
  const list = reachable(campaignRecipients(campaign, ctx), campaign.channel, ctx);

  const sends = (campaign.sends || []).filter(s => s.status === 'sent');
  const after = parseDate(campaign.sentAt || campaign.created);

  /*
   * Real replies, read out of the mailbox the campaign went out from. An
   * automatic answer is not a reply and a bounce is the opposite of one, so
   * only what a person actually wrote counts.
   */
  const answered = repliedAddresses(campaign, ctx);
  const joined = convertedAddresses(campaign, ctx);
  const everyone = campaignRecipients(campaign, ctx);
  const addressOf = new Map(everyone.map(r => [r.id, String(r.email || '').trim().toLowerCase()]));

  /* Counted once per person, whichever signal found them. */
  const already = new Set();

  let replied = 0;
  let converted = 0;
  let revenue = 0;

  sends.forEach(s => {
    const client = rows.find(x => x.id === s.rid);
    const contact = crm.contacts.find(x => x.id === s.rid);

    const wrote = addressOf.get(s.rid);

    /* A marketing contact who has since appeared on the ledger. */
    if (wrote && joined.has(wrote) && !already.has(wrote)) {
      already.add(wrote);
      converted += 1;
      revenue += amountOf(S, joined.get(wrote));
    }

    if (wrote && answered.has(wrote)) {
      replied += 1;
      return;
    }

    /*
     * Date this recipient was actually sent to, not the date the campaign
     * first went out — sending a hundred people over a week must not make the
     * ones reached on day seven look like they answered on day one.
     */
    const sentOn = parseDate(s.at) || after;

    if (client) {
      /*
       * Strictly later. Recording a send stamps lastContacted itself, so
       * a '>=' here counts every message as its own reply and pins the reply
       * rate at 100%. The cost of '>' is that a reply on the send date is
       * missed, which is unavoidable while these dates carry no time.
       */
      const contacted = parseDate(client.lastContacted);
      if (sentOn && contacted && contacted > sentOn) replied++;

      // Payment is never written by the send, so same-day still counts.
      const paid = parseDate(client.lastPaid);
      const key = String(client.email || '').trim().toLowerCase();
      if (sentOn && paid && paid >= sentOn && !already.has(key)) {
        if (key) already.add(key);
        converted++;
        revenue += amountOf(S, client);
      }
    } else if (contact) {
      const won = crm.deals.filter(d => d.contact === contact.id && d.stage === 'Won');
      if (won.length) {
        converted++;
        revenue += won.reduce((t, d) => t + (+d.value || 0), 0);
      }
    }
  });

  /*
   * WhatsApp does not go through the mailbox or the tracking pixel, so its
   * results live in the message log the webhook writes, not in the fields
   * computed above. Read them from there and let them win, so every screen
   * that adds these up - the channel page tiles, the dashboard totals - counts
   * a WhatsApp campaign by what actually happened to it.
   */
  if (campaign.channel === 'push') {
    const p = pushStats(campaign, ctx);
    return {
      reach: p.sent, sent: p.sent, delivered: p.sent, read: 0,
      failed: p.failed, replied: 0, converted, revenue
    };
  }

  if (campaign.channel === 'whatsapp') {
    const wa = waStatus(campaign, ctx);
    const repliers = new Set(waReplies(campaign, ctx).map(m => m.phone));
    return {
      reach: list.length,
      sent: wa.sent || sends.length,
      delivered: wa.delivered,
      read: wa.read,
      failed: wa.failed,
      replied: repliers.size,
      converted,
      revenue
    };
  }

  return {
    reach: list.length,
    sent: sends.length,
    delivered: sends.length,
    read: 0,
    // Refused by the server, which is the other half of a delivery figure.
    failed: (campaign.sends || []).filter(x => x.status === 'failed').length,
    replied,
    converted,
    revenue
  };
}

/**
 * Newest first, for a list somebody adds to.
 *
 * Sorting on `created` alone got this wrong twice over. It is a display string
 * — "02 Oct 2026" sorts before "10 Sep 2026" as text — and it carries no time,
 * so everything made on the same day tied and the sort left them in the order
 * they were appended, putting the one just created at the bottom.
 *
 * `createdAt` is a real instant and is what new records carry. Older ones have
 * only the display date, so that is parsed rather than compared as text, and a
 * remaining tie falls back to position: these lists are appended to, so later
 * in the array means later in time.
 */
export function newestFirst(list) {
  const at = (record, index) => {
    const exact = record.createdAt ? Date.parse(record.createdAt) : NaN;
    if (!Number.isNaN(exact)) return exact;

    const day = parseDate(record.created);
    // Nudge by position so same-day records still come out in the order they
    // were added, newest last in the array and therefore first on screen.
    return day ? day.getTime() + index : index;
  };

  return [...(list || [])]
    .map((record, index) => ({ record, key: at(record, index) }))
    .sort((a, b) => b.key - a.key)
    .map(x => x.record);
}

/** Does a stored schedule carry a time of day, or only a date? */
export const isTimed = schedule => /\d{1,2}:\d{2}/.test(String(schedule || ''));

/**
 * Where a campaign Send-on date sits relative to now.
 *
 * Nothing goes out on a schedule, because there is no delivery pipeline. The
 * date exists to tell a person when to open the send console, so this turns it
 * into something the screen can say out loud. Null means there is nothing to
 * report: no date, one that cannot be read, or a campaign already sent.
 *
 * A schedule with a time on it is measured in minutes and hours rather than
 * days: "in 3h" is the useful thing to know about a 4pm send, and "due today"
 * would still read that way an hour after it was missed.
 */
export function scheduleState(campaign, now = Date.now()) {
  if (!campaign || campaign.status === 'Sent') return null;

  const when = parseDate(campaign.schedule);
  if (!when) return null;

  const days = daysTo(when);
  if (days === null) return null;

  if (isTimed(campaign.schedule)) {
    const mins = Math.round((when.getTime() - now) / 60000);

    if (mins < 0) {
      const late = -mins;
      if (late < 60) return { tone: 'over', days, label: `${late}m overdue` };
      if (late < 1440) return { tone: 'over', days, label: `${Math.round(late / 60)}h overdue` };
      return { tone: 'over', days, label: `${Math.round(late / 1440)}d overdue` };
    }
    if (mins < 60) return { tone: 'due', days, label: `in ${mins}m` };
    if (mins < 1440) return { tone: 'due', days, label: `in ${Math.round(mins / 60)}h` };
    return { tone: 'ahead', days, label: `in ${Math.round(mins / 1440)}d` };
  }

  if (days < 0) return { tone: 'over', days, label: Math.abs(days) + 'd overdue' };
  if (days === 0) return { tone: 'due', days, label: 'due today' };
  return { tone: 'ahead', days, label: 'in ' + days + 'd' };
}

/* --------------------------------------------------------------- journeys */

export function journeyStats(journey, mkt) {
  const enrolled = Object.values(journey.enrolled || {});
  return {
    enrolled: enrolled.length,
    active: enrolled.filter(x => x.status === 'active').length,
    done: enrolled.filter(x => x.status === 'done').length,
    exited: enrolled.filter(x => x.status === 'exited').length,
    pending: (mkt.queue || []).filter(q => q.jid === journey.id && q.status === 'pending').length
  };
}

export function stepLabel(step) {
  if (step.type === 'wait') return `Wait ${step.days || 1}d`;
  if (step.type === 'message') {
    return channelLabel(step.channel) + (step.subject ? `: ${String(step.subject).slice(0, 22)}` : '');
  }
  if (step.type === 'task') return `Task: ${String(step.title || 'follow up').slice(0, 22)}`;
  if (step.type === 'notify') return 'Notify owner';
  if (step.type === 'exit') return `Exit if ${(EXITS.find(e => e[0] === step.when) || ['', 'they pay'])[1]}`;
  return step.type;
}

/** Has an exit condition been met for this record? */
export function exitMet(when, rid, { S, rows, crm, mkt }) {
  const client = rows.find(x => x.id === rid);
  const contact = crm.contacts.find(x => x.id === rid);

  if (when === 'paid' && client) return String(client.pay || '').toUpperCase() === 'YES';
  if (when === 'replied' && client) {
    const last = parseDate(client.lastContacted);
    return !!last && daysAgo(last) <= 2;
  }
  if (when === 'converted') {
    if (contact && crm.deals.some(d => d.contact === contact.id && d.stage === 'Won')) return true;
    return !!(client && deriveClient(S, client).paid);
  }
  if (when === 'optout') {
    const rec = client || contact || {};
    return isOptedOut(mkt, { email: rec.email, phone: rec.phone });
  }
  return false;
}

/**
 * Advance every running journey.
 *
 * Returns a *plan* rather than mutating: the updated journeys, the messages to
 * queue, the tasks to create and the notifications to raise. The store applies
 * it. Keeping it pure is what lets the smoke test run a journey end to end in
 * Node with no React and no clock tricks.
 *
 * The inner `while` is bounded — a journey of nothing but zero-day waits would
 * otherwise spin forever.
 */
export function planJourneyRun(ctx, now = Date.now()) {
  const { S, rows, crm, mkt } = ctx;
  const stamp = formatDate(new Date(now), false, S.dateFmt);

  const journeys = (mkt.journeys || []).map(j => ({ ...j, enrolled: { ...(j.enrolled || {}) } }));
  const queued = [];
  const tasks = [];
  const notify = [];
  let moved = 0;

  journeys.forEach(journey => {
    if (!journey.enabled || !(journey.steps || []).length) return;

    const audience = mkt.audiences.find(a => a.id === journey.audience);
    if (!audience) return;

    const list = audienceOf(audience, ctx);
    const inAudience = new Set(list.map(r => r.id));

    // Enrol anyone new, and re-enrol anyone whose cool-off has elapsed.
    list.forEach(r => {
      const e = journey.enrolled[r.id];
      if (!e) {
        journey.enrolled[r.id] = { step: 0, next: now, status: 'active', at: stamp, history: [] };
        return;
      }
      if (e.status === 'done' && journey.recool) {
        const since = now - new Date(e.doneAt || e.at).getTime();
        if (since > journey.recool * 864e5) {
          journey.enrolled[r.id] = { step: 0, next: now, status: 'active', at: stamp, history: [] };
        }
      }
    });

    Object.entries(journey.enrolled).forEach(([rid, enrolment]) => {
      const e = { ...enrolment, history: [...(enrolment.history || [])] };
      journey.enrolled[rid] = e;
      if (e.status !== 'active') return;

      if (journey.exitOnLeave !== false && !inAudience.has(rid)) {
        e.status = 'exited';
        e.reason = 'left the audience';
        return;
      }

      const record = list.find(r => r.id === rid) || { id: rid, name: '(gone)', vars: {} };
      let guard = 0;

      while (e.status === 'active' && e.next <= now && guard++ < 8) {
        const step = (journey.steps || [])[e.step];
        if (!step) {
          e.status = 'done';
          e.doneAt = stamp;
          break;
        }

        if (step.type === 'wait') {
          e.next = now + (Number(step.days) || 1) * 864e5;
          e.step++;
          continue;
        }

        if (step.type === 'exit') {
          if (exitMet(step.when, rid, ctx)) {
            e.status = 'exited';
            e.reason = (EXITS.find(x => x[0] === step.when) || ['', ''])[1];
            break;
          }
          e.step++;
          continue;
        }

        if (step.type === 'message') {
          const channel = step.channel || 'email';
          if (!isOptedOut(mkt, record) && reachable([record], channel, ctx).length) {
            queued.push({
              jid: journey.id, jname: journey.name, step: e.step, rid,
              name: record.name, email: record.email, phone: record.phone, channel,
              subject: applyMerge(step.subject || '', record),
              body: applyMerge(step.body || '', record),
              due: stamp, status: 'pending'
            });
          }
          e.history.push({ step: e.step, at: stamp, what: `queued ${channel}` });
        }

        if (step.type === 'task') {
          const client = rows.find(x => x.id === rid);
          tasks.push({
            subject: applyMerge(step.title || 'Follow up', record),
            type: 'Call',
            who: step.owner || (client && client.handler) || S.senderName || '',
            dealId: '',
            due: stamp,
            done: false,
            notes: `Created by the "${journey.name}" journey`
          });
        }

        if (step.type === 'notify') {
          notify.push({
            id: `j${journey.id}${rid}`, tag: 'journey', client: record.name,
            txt: `${journey.name} — step ${e.step + 1} reached`, sev: 2, value: 0
          });
        }

        e.step++;
        e.next = now;
        moved++;
      }
    });
  });

  return { journeys, queued, tasks, notify, moved };
}

/**
 * How many distinct people every saved audience covers, and how many of them
 * can actually be contacted on something.
 *
 * Two things were wrong with counting this inline. It never applied
 * `reachable`, so a tile labelled "reachable" was really counting everyone in
 * a segment — opt-outs, blank addresses and all. And it keyed the set on
 * `email || phone`, which for a record carrying neither is `undefined`: every
 * such person collapsed into one phantom entry that the total then counted as
 * a real, contactable human.
 *
 * "Reachable on something" is the honest test for a tile that sits above all
 * four channels — checking email and WhatsApp covers both kinds of address,
 * and each person is judged once, under one key, so nobody is counted twice
 * for having both.
 */
export function audienceCoverage(ctx) {
  const seen = new Set();
  let covered = 0;
  let contactable = 0;

  (ctx.mkt.audiences || []).forEach(a => audienceOf(a, ctx).forEach(r => {
    const key = String(r.email || '').trim().toLowerCase() || digitsOf(r.phone);
    // No address at all: a real person in the segment, but not a distinct one
    // we could ever reach, so they count as covered and nothing more.
    if (!key) { covered++; return; }
    if (seen.has(key)) return;
    seen.add(key);
    covered++;

    if (reachable([r], 'email', ctx).length || reachable([r], 'whatsapp', ctx).length) {
      contactable++;
    }
  }));

  return { covered, contactable };
}

/**
 * The marketing address book: how many are on it, and how many a campaign
 * could actually reach.
 *
 * This is the figure the module's tiles show, because it is the one that
 * answers "who can I send to today". A saved segment covers hundreds of
 * accounts on the client ledger, but the campaign wizard targets this list —
 * so counting the segments there told somebody they could reach six hundred
 * people when the answer was one.
 */
export function contactBook(ctx) {
  const list = audienceOf({ src: 'marketing' }, ctx);

  const contactable = list.filter(r =>
    reachable([r], 'email', ctx).length || reachable([r], 'whatsapp', ctx).length).length;

  return { total: list.length, contactable };
}

/* -------------------------------------------------------------- influence */

/**
 * What marketing is worth: the MRR and pipeline attached to everyone it has
 * actually touched, whether through a campaign send or a journey step.
 */
export function marketingInfluence({ S, rows, crm, mkt }) {
  const touched = new Set();

  mkt.campaigns.forEach(c =>
    (c.sends || []).filter(s => s.status === 'sent').forEach(s => touched.add(s.rid)));

  (mkt.journeys || []).forEach(j =>
    Object.entries(j.enrolled || {}).forEach(([rid, e]) => {
      if ((e.history || []).length) touched.add(rid);
    }));

  let mrr = 0;
  let paying = 0;
  let pipeline = 0;

  touched.forEach(rid => {
    const client = rows.find(x => x.id === rid);
    if (client) {
      const d = deriveClient(S, client);
      if (d.paid) {
        paying++;
        mrr += mrrOf(S, client, d);
      }
    }
    const contact = crm.contacts.find(x => x.id === rid);
    if (contact) {
      pipeline += crm.deals
        .filter(d => d.contact === contact.id && isOpen(d))
        .reduce((t, d) => t + (+d.value || 0), 0);
    }
  });

  return { touched: touched.size, paying, mrr: Math.round(mrr), pipeline: Math.round(pipeline) };
}

/** Totals per channel for the Analytics tab. */
export function channelBreakdown(ctx) {
  const byChannel = {};

  ctx.mkt.campaigns.forEach(c => {
    const st = campaignStats(c, ctx);
    const k = c.channel;

    byChannel[k] = byChannel[k]
      || { camps: 0, reach: 0, sent: 0, delivered: 0, read: 0, failed: 0, replied: 0, converted: 0, revenue: 0 };

    byChannel[k].camps++;
    byChannel[k].reach += st.reach;
    byChannel[k].sent += st.sent;
    byChannel[k].delivered += st.delivered || 0;
    byChannel[k].read += st.read || 0;
    byChannel[k].failed += st.failed;
    byChannel[k].replied += st.replied;
    byChannel[k].converted += st.converted;
    byChannel[k].revenue += st.revenue;
  });

  return byChannel;
}

/**
 * The module's headline figures, across every channel.
 *
 * "Delivered" here means the mail server accepted it — the only delivery fact
 * an SMTP send actually knows. Whether it reached an inbox, and whether it was
 * opened, needs tracking, and those are left absent rather than guessed at.
 */
export function marketingTotals(ctx) {
  const byChannel = channelBreakdown(ctx);

  const total = Object.values(byChannel).reduce((t, v) => ({
    camps: t.camps + v.camps,
    reach: t.reach + v.reach,
    sent: t.sent + v.sent,
    delivered: t.delivered + (v.delivered || 0),
    read: t.read + (v.read || 0),
    failed: t.failed + v.failed,
    replied: t.replied + v.replied,
    converted: t.converted + v.converted,
    revenue: t.revenue + v.revenue
  }), { camps: 0, reach: 0, sent: 0, delivered: 0, read: 0, failed: 0, replied: 0, converted: 0, revenue: 0 });

  const attempted = total.sent + total.failed;

  /* Opens and clicks across every campaign, counted per person per campaign
     so one enthusiast cannot lift the rate on their own. A click counts as a
     read for the same reason it does on a campaign: images are blocked by
     default, and pressing a link is the firmer evidence of the two. */
  const reached = new Set();
  ((ctx.mkt && ctx.mkt.campaigns) || []).forEach(c => {
    (c.sends || []).forEach(x => {
      if (x.status === 'sent') reached.add(`${c.id}|${x.rid}`);
    });
  });

  const seenOpen = new Set();
  const seenClick = new Set();
  (ctx.events || []).forEach(e => {
    const key = `${e.campaign_id}|${e.rid}`;
    // No send behind it, nothing to be a rate of. See campaignEngagement.
    if (!reached.has(key)) return;
    if (e.kind === 'open') seenOpen.add(key);
    if (e.kind === 'click') { seenClick.add(key); seenOpen.add(key); }
  });

  /*
   * "Read" is email opens plus WhatsApp reads - a WhatsApp read receipt is the
   * same fact an email open is trying to be, only certain. A click still
   * counts as a read on the email side, for the reason above.
   */
  const emailOpened = seenOpen.size;
  const read = emailOpened + total.read;

  /*
   * Delivery: WhatsApp knows real delivery from the phone; email and push only
   * know the server accepted it. Each channel's own `delivered` already carries
   * the best fact it has — email and push set it to their sent count, WhatsApp
   * to the real delivered count — so the sum of those, total.delivered, is the
   * whole delivered figure. Adding total.sent on top (as this once did) counted
   * email and push twice and drove the rate past 100%.
   */
  const delivered = total.delivered;

  return {
    ...total,
    byChannel,
    // Real WhatsApp delivery, plus email's and push's "server accepted", over
    // everything attempted. Delivered can never exceed attempted, but the cap
    // guards the figure against any future double-count creeping back in.
    delivery: attempted ? Math.min(100, Math.round((delivered / attempted) * 100)) : null,
    replyRate: total.sent ? Math.round((total.replied / total.sent) * 100) : null,
    bounced: (ctx.replies || []).filter(r => r.kind === 'bounce').length,
    opened: read,
    clicked: seenClick.size,
    openRate: total.sent ? Math.round((read / total.sent) * 100) : null,
    // Clicks are an email idea; WhatsApp has none. Null when there is no email
    // campaign to have earned any, so the tile can say so rather than show 0%.
    clickRate: (byChannel.email && byChannel.email.sent)
      ? Math.round((seenClick.size / byChannel.email.sent) * 100)
      : null
  };
}
